// @flow
import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  InfoOutlined,
  CloseOutlined,
  TrendingUpOutlined,
} from '@material-ui/icons';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import IconButton from '@material-ui/core/IconButton';
import {Link} from 'react-router-dom';

import {colors} from '../../../assets/styles/colors';
import {useUser} from '../../auth/utils';
import {useIsFeatureInTier} from '../../../utils/component';
import {getTierName, useTierStoreName} from '../../store/utils/utils_tier';
import {classNames} from '../../../utils/react/className';
import {useSessionStorage} from '../../../utils/dom';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import type {StoreFeature} from '../../../entities/store-feature';
import {formatDate} from '../../../utils/date';

type Props = {
  feature: StoreFeature,
  plans: string | Array<string>,
  availableUntil?: Date,
  variant?: 'info' | 'warning' | 'success',
};

const useStyles = makeStyles(theme => ({
  alertContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    borderRadius: '0.75rem',
    marginBottom: '1rem',
    border: '1px solid',
    backgroundColor: colors.grey100,
    borderColor: colors.info,
    transition: 'all 0.2s ease-in-out',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.75rem',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 163, 255, 0.15)',
    },
  },
  alertContainerWarning: {
    borderColor: colors.warning,
    backgroundColor: '#FFFBF5',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(253, 172, 66, 0.15)',
    },
  },
  alertContainerSuccess: {
    borderColor: colors.success,
    backgroundColor: '#F0FFF9',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 178, 130, 0.15)',
    },
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: colors.info,
  },
  iconWrapperWarning: {
    backgroundColor: colors.warning,
  },
  iconWrapperSuccess: {
    backgroundColor: colors.success,
  },
  icon: {
    fontSize: '1.125rem',
    color: 'white',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  contentWrapper: {
    flex: 1,
    display: 'flex',
    gap: '0.25rem',
    minWidth: 0,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.grey1000,
    lineHeight: 1.4,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8125rem',
    },
  },
  message: {
    fontSize: '0.8125rem',
    color: colors.grey700,
    lineHeight: 1.5,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  planBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.125rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: colors.lightPurple,
    color: colors.primary,
    marginLeft: '0.25rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6875rem',
      padding: '0.0625rem 0.375rem',
    },
  },
  timeWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    marginTop: '0.25rem',
    [theme.breakpoints.down('sm')]: {
      gap: '0.25rem',
    },
  },
  timeIcon: {
    fontSize: '0.875rem',
    color: colors.grey600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  timeText: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6875rem',
    },
  },
  actionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.75rem',
  },
  upgradeButton: {
    backgroundColor: colors.primary,
    color: 'white',
    fontSize: '0.8125rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '0.375rem 1.25rem',
    boxShadow: '0 2px 8px rgba(123, 57, 255, 0.2)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.purple4,
      boxShadow: '0 4px 12px rgba(123, 57, 255, 0.3)',
      transform: 'translateY(-1px)',
    },
  },
  upgradeIcon: {
    fontSize: '1rem',
    marginRight: '0.25rem',
  },
  closeButton: {
    position: 'absolute',
    right: '0.5rem',
    top: '0.5rem',
    color: colors.grey600,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      color: colors.grey800,
    },
  },
  closeIcon: {
    fontSize: '1.125rem',
  },
}));

export default function TemporaryFeatureAlert({
  feature,
  plans,
  availableUntil,
  variant = 'info',
}: Props) {
  const classes = useStyles();
  const user = useUser();
  const hasFeature = useIsFeatureInTier(feature);
  const userTier = useTierStoreName();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  // Session storage key based on feature to track dismissal per feature
  const storageKey = `temporary-feature-alert-dismissed-${feature}`;
  const [isDismissed, setIsDismissed] = useSessionStorage(storageKey, false);

  if (!user) {
    return null;
  }

  if (!hasFeature) {
    return null;
  }

  const plansArray = Array.isArray(plans) ? plans : [plans];
  const isInPermanentPlan = plansArray.includes(userTier);

  if (isInPermanentPlan) {
    return null;
  }

  // Don't show if user has dismissed the alert in this session
  if (isDismissed) {
    return null;
  }

  const handleClose = () => {
    setIsDismissed(true);
  };

  // Get plan names and determine if singular or plural
  const planNames = plansArray.map(plan => getTierName(plan)).join(', ');
  const isSinglePlan = plansArray.length === 1;

  const containerClass = classNames(
    classes.alertContainer,
    variant === 'warning' && classes.alertContainerWarning,
    variant === 'success' && classes.alertContainerSuccess
  );

  const iconWrapperClass = classNames(
    classes.iconWrapper,
    variant === 'warning' && classes.iconWrapperWarning,
    variant === 'success' && classes.iconWrapperSuccess
  );

  const formattedDate = availableUntil
    ? formatDate(availableUntil, 'DD MMMM YYYY')
    : null;

  return (
    <div className={containerClass}>
      <div className={classes.contentWrapper}>
        <div className={iconWrapperClass}>
          <InfoOutlined className={classes.icon} />
        </div>
        <p className={classes.title}>
          {isMobile ? '¡Acceso temporal!' : '¡Acceso temporal disponible!'}
        </p>
      </div>
      <div className={classes.contentWrapper}>
        <p className={classes.message}>
          {formattedDate ? (
            <>
              Acceso temporal hasta <strong>{formattedDate}</strong>. Después se
              pausará, pero todo se guardará para cuando tengas{' '}
              {isSinglePlan ? 'el plan' : 'uno de los planes'}
              <span className={classes.planBadge}>{planNames}</span>.
            </>
          ) : (
            <>
              Esta funcionalidad está disponible temporalmente. Pronto se
              pausará, pero todo se guardará para cuando tengas{' '}
              {isSinglePlan ? 'el plan' : 'uno de los planes'}
              <span className={classes.planBadge}>{planNames}</span>.
            </>
          )}
        </p>
        <div className={classes.actionsWrapper}>
          <CustomButton
            variant="contained"
            className={classes.upgradeButton}
            component={Link}
            to="/tiers-list"
            size="small"
            target="_blank"
          >
            <TrendingUpOutlined className={classes.upgradeIcon} />
            Ver Planes
          </CustomButton>
        </div>
      </div>
      <IconButton
        className={classes.closeButton}
        onClick={handleClose}
        aria-label="Cerrar alerta"
      >
        <CloseOutlined className={classes.closeIcon} />
      </IconButton>
    </div>
  );
}
